import type { Map as MapboxMap } from "mapbox-gl";
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Skeleton } from "../components/ui/Skeleton";
import { cellsForViewport } from "../features/map/geohash";
import { useGeolocation } from "../features/map/useGeolocation";
import type { CellsChangedEvent } from "../features/map/ViewportTracker";
import { channelManager } from "../features/realtime/ChannelManager";
import { healthMonitor } from "../features/realtime/HealthMonitor";
import { usePolling } from "../features/realtime/usePolling";
import type { StationListItem } from "../features/stations/api";
import { useStationsNearby } from "../features/stations/hooks";
import { StationList, type SortMode } from "../features/stations/StationList";
import { TagSearchBox } from "../features/stations/TagSearchBox";
import { useDebounce } from "../lib/useDebounce";

// Lazy boundary: defers the mapbox-gl chunk until /chargers mounts.
// Every other route saves that download on first paint.
const MapSection = lazy(() => import("../features/map/MapSection"));

const FALLBACK_ZOOM = 12;

export function ChargersRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [map, setMap] = useState<MapboxMap | null>(null);
  const [viewportCells, setViewportCells] = useState<string[]>([]);
  const [unhealthy, setUnhealthy] = useState(false);
  // Nearest is the default — the page is "ESTACIONES MÁS CERCANAS".
  const [sort, setSort] = useState<SortMode>("nearest");
  // The URL is the single source of truth for the tag filter — topbar search,
  // in-route TagSearchBox edits, and browser back/forward all flow through it.
  // Push the FIRST edit after any external navigation (e.g. topbar submit pushed
  // /?tag=cafe — pressing Back should return there, not skip past it); replace
  // every subsequent keystroke in the same edit chain so "mall" → "mall food"
  // collapses to a single history entry. lastEditSearchStr records the search
  // string we left after our last write; if currentSearch doesn't match it, an
  // external navigation happened in between and the next edit must push.
  const tagQuery = searchParams.get("tag") ?? "";
  const lastEditSearchStr = useRef<string | null>(null);
  const setTagQuery = useCallback(
    (next: string) => {
      const isChainEdit = lastEditSearchStr.current === searchParams.toString();
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next) params.set("tag", next);
          else params.delete("tag");
          lastEditSearchStr.current = params.toString();
          return params;
        },
        { replace: isChainEdit },
      );
    },
    [searchParams, setSearchParams],
  );
  const debouncedTagQuery = useDebounce(tagQuery, 300);

  const location = useGeolocation();
  const fallbackCells = useMemo(
    () => cellsForViewport({ lat: location.lat, lng: location.lng }, FALLBACK_ZOOM),
    [location.lat, location.lng],
  );

  const activeCells = viewportCells.length > 0 ? viewportCells : fallbackCells;
  const cellsRef = useRef<string[]>(activeCells);

  useEffect(() => healthMonitor.subscribe(setUnhealthy), []);
  useEffect(() => () => channelManager.setActiveCells([]), []);

  // Single owner: every cell change — viewport or fallback — funnels here.
  useEffect(() => {
    cellsRef.current = activeCells;
    channelManager.setActiveCells(activeCells);
  }, [activeCells]);

  const handleCellsChanged = useCallback((event: CellsChangedEvent) => {
    setViewportCells(event.current);
  }, []);

  usePolling(cellsRef, unhealthy);

  const { data: stations = [] } = useStationsNearby(activeCells, debouncedTagQuery);

  const handleMarkerClick = useCallback(
    (station: StationListItem) => navigate(`/stations/${station.id}`),
    [navigate],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="sticky top-14 z-10 -mx-5 border-b border-line bg-bg/90 px-5 pb-3 pt-2 backdrop-blur md:top-0 md:mx-0 md:px-0">
        <div className="mb-2 flex items-center justify-end">
          <Link
            to="/home"
            className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-fg-mute transition-colors duration-200 hover:border-volt/60 hover:text-volt"
          >
            <svg viewBox="0 0 14 14" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="7" cy="7" r="5.5" />
              <path d="M7 4.5v3M7 9.5v.2" strokeLinecap="round" />
            </svg>
            {t("nav.howto")}
          </Link>
        </div>
        <TagSearchBox value={tagQuery} onChange={setTagQuery} />
      </div>
      <div
        data-testid="chargers-body"
        className="xl:grid xl:grid-cols-[1fr_360px] xl:items-start xl:gap-6"
      >
        <Suspense fallback={<Skeleton className="h-[60vh] min-h-64 w-full md:h-[70vh]" />}>
          <MapSection
            unhealthyMessage={unhealthy ? t("realtime.unhealthy_banner") : null}
            stations={stations}
            map={map}
            onMapReady={setMap}
            onCellsChanged={handleCellsChanged}
            onMarkerClick={handleMarkerClick}
          />
        </Suspense>
        <div className="mt-4 space-y-3 xl:mt-0">
          <ListHeading
            tagQuery={debouncedTagQuery.trim()}
            count={stations.length}
            sort={sort}
            onSortChange={setSort}
          />
          <StationList
            stations={stations}
            sort={sort}
            origin={{ lat: location.lat, lng: location.lng }}
          />
        </div>
      </div>
    </div>
  );
}

type ListHeadingProps = {
  tagQuery: string;
  count: number;
  sort: SortMode;
  onSortChange: (sort: SortMode) => void;
};

function ListHeading({ tagQuery, count, sort, onSortChange }: ListHeadingProps) {
  const { t } = useTranslation();
  const subheading =
    tagQuery.length > 0
      ? t("chargers.list_subheading_tag_mode", { query: tagQuery })
      : sort === "nearest"
        ? t("chargers.list_subheading_nearest")
        : t("chargers.list_subheading_fast");
  return (
    <header className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="label-caps text-fg-mute">{t("chargers.list_heading")}</p>
          <p className="mt-1 text-xs text-fg-faint">{subheading}</p>
        </div>
        <p className="num text-volt">
          <span className="text-base font-semibold">{count}</span>
          <span className="ml-1 text-[11px] uppercase tracking-[0.14em] text-fg-mute">
            {t("chargers.count_nearby")}
          </span>
        </p>
      </div>
      <SortToggle sort={sort} onSortChange={onSortChange} />
    </header>
  );
}

function SortToggle({ sort, onSortChange }: { sort: SortMode; onSortChange: (s: SortMode) => void }) {
  const { t } = useTranslation();
  const options: { value: SortMode; label: string }[] = [
    { value: "nearest", label: t("chargers.sort_nearest") },
    { value: "fast", label: t("chargers.sort_fast") },
  ];
  return (
    <div
      role="group"
      aria-label={t("chargers.sort_aria")}
      className="inline-flex rounded-full border border-line bg-surface p-0.5 text-xs"
    >
      {options.map((opt) => {
        const active = sort === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onSortChange(opt.value)}
            className={`rounded-full px-3 py-1 font-medium transition-colors ${
              active
                ? "bg-volt/10 text-volt-deep"
                : "text-fg-mute hover:text-fg"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
